import { Box, Text, useColorModeValue } from '@ecoinvent/ui';

import { formatNumericalValue } from 'utilities/numbers';

import { COLORS, SUPPLY_CARD_TEXT_INDENT } from './constants';

export type ImpactScoreState =
  | { status: 'notAsked' }
  | { status: 'pending' }
  | { status: 'empty' }
  | { status: 'error' }
  | { status: 'ready'; score: number; unit: string };

// `isLive` only on the root and selected card, or every filter change is announced once per card.
type Props = { state: ImpactScoreState; isLive?: boolean };

// Token names, not raw values: a raw `14px` makes an undefined CSS var and silently voids the calc.
const SCORE_FONT_SIZE = 'sm';
const SCORE_LINE_HEIGHT = 'base';
const SCORE_ROW_HEIGHT = `calc(var(--chakra-fontSizes-${SCORE_FONT_SIZE}) * var(--chakra-lineHeights-${SCORE_LINE_HEIGHT}))`;

const ImpactScore = ({ state, isLive }: Props) => {
  const scoreColor = useColorModeValue(COLORS.textLight, COLORS.textDark);
  const dimColor = useColorModeValue(COLORS.grayDark, COLORS.grayDim);

  const renderState = () => {
    if (state.status === 'notAsked' || state.status === 'pending') {
      return (
        <Box pl={`${SUPPLY_CARD_TEXT_INDENT}px`} h={SCORE_ROW_HEIGHT} display="flex" alignItems="center">
          <Box bg={dimColor} opacity={0.25} borderRadius="sm" h="12px" w="55%" />
        </Box>
      );
    }

    if (state.status === 'empty' || state.status === 'error') {
      return (
        <Text
          pl={`${SUPPLY_CARD_TEXT_INDENT}px`}
          fontSize={SCORE_FONT_SIZE}
          lineHeight={SCORE_LINE_HEIGHT}
          color={dimColor}
          noOfLines={1}
        >
          {state.status === 'empty' ? 'No score for this selection' : 'Score unavailable'}
        </Text>
      );
    }

    return (
      <Text
        pl={`${SUPPLY_CARD_TEXT_INDENT}px`}
        fontSize={SCORE_FONT_SIZE}
        lineHeight={SCORE_LINE_HEIGHT}
        fontWeight="bold"
        color={scoreColor}
        noOfLines={1}
      >
        {formatNumericalValue(state.score)}{' '}
        <Text as="span" fontWeight="normal" color={dimColor}>
          {state.unit}
        </Text>
      </Text>
    );
  };

  // One wrapper across every state: screen readers miss a live region inserted already populated.
  return (
    <Box minH={SCORE_ROW_HEIGHT} role={isLive ? 'status' : undefined} aria-live={isLive ? 'polite' : undefined}>
      {renderState()}
    </Box>
  );
};

export default ImpactScore;
